// src/context/SettingsContext.tsx
/**
 * Settings Context - Manages user settings state
 *
 * MIGRATION NOTE (January 2026):
 * Migrated from REST API to Convex for real-time settings management.
 * Settings are now fetched and updated via Convex queries/mutations.
 *
 * Benefits:
 * - Real-time sync across tabs via Convex subscriptions
 * - No REST API calls needed
 * - Automatic optimistic updates
 */

import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { networkUtils } from '../utils/networkUtils';
import { useToast } from './ToastContext';
import { createLogger } from '../utils/logger-ssr-safe';
import { useAuth } from './AuthContext';

const logger = createLogger({ module: 'Settings', level: 'info' });

interface Settings {
  defaultCareerCardsView: 'grid' | 'kanban';
  files_view_mode: string;
  sidebarCollapsed: boolean;
  darkMode: boolean;
  timezone: string;
  dashboard_tour_completed?: boolean;
  resumeCTADismissed?: boolean;
}

interface PreloadedSettings {
  settings?: Settings | null;
  unified_home_settings?: {
    viewMode?: string;
    sortBy?: string;
    groupByType?: boolean;
    itemsPerPage?: number;
    scrollMode?: 'pagination' | 'infinite';
    [key: string]: unknown;
  } | null;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => Promise<void>;
  isOffline: boolean;
  preloadUserSettings: (email: string, preloadedData?: PreloadedSettings) => Promise<void>;
}

const defaultSettings: Settings = {
  defaultCareerCardsView: 'grid',
  sidebarCollapsed: false,
  files_view_mode: 'grid',
  darkMode: false,
  timezone: 'UTC',
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const PUBLIC_PATHS = ['/', '/login', '/terms', '/privacy', '/gamification-support'];

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const { user, isAuthenticated } = useAuth();

  // Subscribe to user settings via Convex - provides real-time updates
  const convexSettings = useQuery(
    api.settings.get,
    isAuthenticated ? {} : 'skip'
  );

  // Mutation for updating settings
  const updateSettingsMutation = useMutation(api.settings.update);

  // Track offline status
  const [isOffline, setIsOffline] = React.useState(networkUtils.isOffline());

  // Set up network connectivity listener
  React.useEffect(() => {
    networkUtils.init();
    return networkUtils.onConnectivityChange((offline) => {
      setIsOffline(offline);
      // Only show toast messages if not on a public page
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
      if (!PUBLIC_PATHS.includes(currentPath)) {
        if (offline) {
          showToast('You are currently offline. Some features may be unavailable.', 'info');
        } else {
          showToast('Back online!', 'success');
        }
      }
    });
  }, [showToast]);

  // Transform Convex settings to the format expected by the app
  const settings: Settings = useMemo(() => {
    if (!convexSettings) {
      return defaultSettings;
    }

    // Map Convex schema fields to app settings interface
    // The userSettings table has both top-level fields and a generic `settings` object
    const genericSettings = convexSettings.settings as Record<string, unknown> | undefined;

    return {
      defaultCareerCardsView:
        (genericSettings?.defaultCareerCardsView as 'grid' | 'kanban') ??
        defaultSettings.defaultCareerCardsView,
      files_view_mode:
        convexSettings.filesViewMode ??
        (genericSettings?.files_view_mode as string) ??
        defaultSettings.files_view_mode,
      sidebarCollapsed:
        convexSettings.sidebarCollapsed ??
        (genericSettings?.sidebarCollapsed as boolean) ??
        defaultSettings.sidebarCollapsed,
      darkMode:
        convexSettings.theme === 'dark' ||
        ((genericSettings?.darkMode as boolean) ?? defaultSettings.darkMode),
      timezone:
        convexSettings.timezone ??
        (genericSettings?.timezone as string) ??
        defaultSettings.timezone,
      dashboard_tour_completed:
        (genericSettings?.dashboard_tour_completed as boolean) ?? false,
      resumeCTADismissed:
        (genericSettings?.resumeCTADismissed as boolean) ?? false,
    };
  }, [convexSettings]);

  // Update settings via Convex mutation
  const updateSettings = useCallback(async (newSettings: Partial<Settings>) => {
    logger.debug('updateSettings called with:', { newSettings, userId: user?.id });

    // Exit early if no user
    if (!user || !isAuthenticated) {
      logger.warn('updateSettings: No user available', { user: !!user, isAuthenticated });
      return;
    }

    try {
      // Map app settings interface to Convex schema
      const updates: Parameters<typeof updateSettingsMutation>[0] = {};

      // Handle theme preference
      if (newSettings.darkMode !== undefined) {
        updates.theme = newSettings.darkMode ? 'dark' : 'light';
      }

      // Handle other top-level fields
      if (newSettings.timezone !== undefined) {
        updates.timezone = newSettings.timezone;
      }

      if (newSettings.sidebarCollapsed !== undefined) {
        updates.sidebarCollapsed = newSettings.sidebarCollapsed;
      }

      if (newSettings.files_view_mode !== undefined) {
        updates.filesViewMode = newSettings.files_view_mode;
      }

      // Store remaining settings in the generic settings object
      const genericUpdates: Record<string, unknown> = {};
      if (newSettings.defaultCareerCardsView !== undefined) {
        genericUpdates.defaultCareerCardsView = newSettings.defaultCareerCardsView;
      }
      if (newSettings.dashboard_tour_completed !== undefined) {
        genericUpdates.dashboard_tour_completed = newSettings.dashboard_tour_completed;
      }
      if (newSettings.resumeCTADismissed !== undefined) {
        genericUpdates.resumeCTADismissed = newSettings.resumeCTADismissed;
      }

      // Merge with existing generic settings if we have updates
      if (Object.keys(genericUpdates).length > 0) {
        const existingGeneric = (convexSettings?.settings as Record<string, unknown>) ?? {};
        updates.settings = {
          ...existingGeneric,
          ...genericUpdates,
        };
      }

      logger.debug('Calling Convex settings.update with:', updates);

      await updateSettingsMutation(updates);

      logger.debug('Settings updated successfully via Convex');
    } catch (error) {
      logger.error('Error updating settings:', undefined, {
        error: error instanceof Error ? error.message : String(error),
        userId: user?.id
      });
      throw error;
    }
  }, [user, isAuthenticated, updateSettingsMutation, convexSettings?.settings]);

  // Legacy function - not needed with Convex but kept for API compatibility
  const preloadUserSettings = useCallback(async (_email: string, _preloadedData?: PreloadedSettings) => {
    // With Convex, settings are automatically loaded via the useQuery hook
    // This function is kept for backward compatibility but does nothing
    logger.debug('preloadUserSettings called - no-op with Convex');
  }, []);

  // Use useMemo to prevent unnecessary re-creation of the context value
  const value = useMemo(() => ({
    settings,
    updateSettings,
    isOffline,
    preloadUserSettings,
  }), [settings, updateSettings, isOffline, preloadUserSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
